/** マイページ開設 */
import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import RowTemplate from '../atoms/RowTemplate';
import axios from '../axios';
import { Row, Col } from 'react-bootstrap';
import { handleBrowzerBack } from '../BrowzerBack';
import LoadingModal from '../molecules/LoadingModal';
import { http_status, module_name, api_response_type } from '../Const';
import { PostData } from '../types/Template';
import { jumpUrl } from '../Common';

const NypageEntry: React.FC = () => {
  // マイページ開設同意画面のリンク先
  const back_url = `${process.env.MIX_MOCK_URL}/agreement`;

  // ログインIDのstate
  const [login_id, setLoginId] = useState('');
  // パスワード
  const [password, setPassword] = useState('');
  // パスワード(確認)
  const [password_confirmation, setPasswordConfirmation] = useState('');
  // マイページ開設開設結果
  const [mypage_open_result, setMypageOpenResult] = useState([]);
  // HTTPステータスコード
  const [status_code, setStatusCode] = useState(0);
  // Ajax通信中フラグ
  const [is_connecting, setIsConnecting] = useState(false);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);
  // 案件情報
  const [reception_id, setReceptionId] = useState(0);
  const [reception_cd, setReceptionCd] = useState('');

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowzerBack();
    getReceptionInfo();
  }, []);

  /**
   * 案件情報を取得する
   */
  const getReceptionInfo = async () => {
    try {
      setIsLoading(true);
      // 案件情報取得
      const response = await axios.get('/entry/reception_info');
      setReceptionId(response.data.reception_id);
      setReceptionCd(response.data.reception_cd);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginIdChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(event.target.value);
  }, []);

  const handlePasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const handlePasswordConfirmationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordConfirmation(event.target.value);
    },
    []
  );

  // マイページ開設ボタンのクリックイベントを定義
  const handleClick = () => {
    // ログイン情報をStateへセット
    const send_mypage_info = {
      login_id: login_id,
      password: password,
      password_confirmation: password_confirmation,
    };
    const mypageOpen = async () => {
      try {
        if (is_connecting) {
          return;
        }
        //ローディング画面表示
        setIsLoading(true);
        setIsConnecting(true);
        const response = await axios.post('/entry/entry_proc', send_mypage_info);
        // 成功
        if (response.status === http_status.created) {
          requestModule();
        }
        setIsConnecting(false);
      } catch (error) {
        jumpUrl(error.response.data.url);
        // 失敗
        setMypageOpenResult(error.response.data.message);
        setStatusCode(error.response.status);
        setIsConnecting(false);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    // パスワード変更処理
    mypageOpen();
  };

  /**
   * API呼び出し処理
   */
  const requestModule = async () => {
    // APIにデータをPOSTする
    const post_data: PostData = {
      reception_id: reception_id,
      reception_cd: reception_cd,
      module_name: module_name.mypage_open,
      detail: [],
      is_json: true,
    };
    try {
      const response = await axios.post('/api/common', post_data);
      // 正常なレスポンス
      if (
        response.data.result &&
        response.data.response_type === api_response_type.transition_to_mypage_complete
      ) {
        // マイページ開設完了画面へ遷移
        location.replace(`${process.env.MIX_MOCK_URL}/entry/complete`);
        // 異常なレスポンス
      } else {
        console.log(response.data);
      }
    } catch (error) {
      console.log(error);
      jumpUrl(error.response.data.url);
    } finally {
      setIsLoading(false);
      setIsConnecting(false);
    }
  };

  return (
    <>
      <div className="container mx-auto col-11">
        <div className="row">
          <div className="col-12 col-md-12 mx-auto my-2">
            <b>次に、ログインに使用するメールアドレス、パスワードを入力してください。</b>
          </div>
        </div>
        <h4 className="title p-2 my-2">マイページ開設</h4>
        <div>
          <p className="p-lead">仮申込時のメールアドレスを入力してください。</p>
        </div>
        <div>
          <form className="form" method="post">
            <RowTemplate
              label="ログインID"
              content={
                <div className="p-title-with-input">
                  <div className="p-text-with-input">
                    <div className="text-with-input-box">
                      <input
                        className="c-input input-l"
                        name="login_id"
                        type="text"
                        placeholder="example@example.com"
                        onChange={handleLoginIdChange}
                        maxLength={200}
                      />
                    </div>
                  </div>
                </div>
              }
            />
            <div>※入力いただくメールアドレス宛に重要なお知らせをお届けします。</div>
            <div className="mt-3">ログインするためのパスワードを設定してください。</div>
            <div>
              <RowTemplate
                label="パスワード"
                content={
                  <div className="p-title-with-input">
                    <div className="p-text-with-input">
                      <div className="text-with-input-box">
                        <input
                          className="c-input input-l"
                          name="password"
                          type="password"
                          placeholder="XXXXXX"
                          onChange={handlePasswordChange}
                          maxLength={200}
                        />
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
            <RowTemplate
              label="パスワード（確認用）"
              content={
                <div className="p-title-with-input">
                  <div className="p-text-with-input">
                    <div className="text-with-input-box">
                      <input
                        className="c-input input-l"
                        name="password_confirmation"
                        type="password"
                        placeholder="XXXXXX"
                        onChange={handlePasswordConfirmationChange}
                        maxLength={200}
                      />
                    </div>
                  </div>
                </div>
              }
            />
            <p>※パスワードは、英字・数字を含む8文字以上で入力してください。</p>
            <div className="mt-3"></div>
            {mypage_open_result !== undefined &&
              // マイページ開設結果メッセージあり
              // @see HTTPステータスコード一覧 - Symfony\Component\HttpFoundation\Response
              status_code >= 400 &&
              status_code <= 511 && (
                // 失敗
                <Row className="d-flex align-items-center justify-content-center">
                  <Col></Col>
                  <Col md={9} className="alert-danger pt-3">
                    <ul>
                      {mypage_open_result.map((error_message, index) => {
                        return <li key={index}>{error_message}</li>;
                      })}
                    </ul>
                  </Col>
                </Row>
              )}
            <div className="p-next-btn-box">
              <button
                className="c-btn btn-max btn-main-color"
                type="button"
                onClick={handleClick}
                data-jump_to="#"
              >
                マイページを開設する
              </button>
            </div>
          </form>

          <div className="row">
            <div className="col-12 col-md-12">
              <div className="p-back-btn-box">
                <a className="c-btn btn-large btn-main-color mx-auto" data-back href={back_url}>
                  戻る
                </a>
              </div>
            </div>
            <div className="col-md-4 ms-auto"></div>
          </div>
        </div>
      </div>
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </>
  );
};

const container = document.getElementById('mypage-entry');
if (container) {
  const root = createRoot(container);
  root.render(<NypageEntry />);
}
